import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent } from '@shopify/events';
import {
  createState,
  counts,
  total,
  remaining,
  isComplete,
  add,
  removeOne,
  resize,
  describe,
  resolveVariantId,
} from '@theme/byo-state';

/**
 * Build Your Own Box builder.
 *
 * Holds no derived state: every render reads from `#state`, which byo-state.js owns. Cart
 * I/O and DOM updates live here; all the rules live in the pure module, where they are
 * unit-tested.
 *
 * The cart line is deliberately a single item — the box variant — with the flavour list
 * carried in line-item properties. That is what keeps the price flat, the cart tidy and
 * checkout entirely native.
 */
class ByoBoxBuilder extends Component {
  requiredRefs = ['config', 'counter', 'progress', 'submit'];

  /** @type {import('@theme/byo-state').State} */
  #state;
  #config;
  #busy = false;
  #addedTimer;

  connectedCallback() {
    super.connectedCallback();

    try {
      this.#config = JSON.parse(this.refs.config.textContent);
    } catch {
      return; // Malformed config: leave the noscript fallback in place.
    }

    const first = this.#config.sizes?.[0];
    this.#state = createState(first ? first.count : 0);
    this.#render();
  }

  #cards() {
    return this.refs.card ?? [];
  }

  #titles() {
    return Object.fromEntries(this.#cards().map((c) => [c.dataset.handle, c.dataset.title]));
  }

  increment(event) {
    const handle = event.target.closest('[data-handle]')?.dataset.handle;
    if (!handle) return;
    this.#state = add(this.#state, handle);
    this.#clearNotices();
    this.#render();
  }

  decrement(event) {
    const handle = event.target.closest('[data-handle]')?.dataset.handle;
    if (!handle) return;
    this.#state = removeOne(this.#state, handle);
    this.#clearNotices();
    this.#render();
  }

  changeSize(event) {
    const size = Number(event.target.value);
    if (!Number.isFinite(size)) return;

    const { state, removed } = resize(this.#state, size);
    this.#state = state;

    if (removed.length) {
      const titles = this.#titles();
      const names = removed.map((h) => titles[h] ?? h).join(', ');
      this.#notice(this.#config.text.noticeTrimmed.replaceAll('[removed]', names));
    } else {
      this.#clearNotices();
    }

    this.#render();
  }

  /** The size entry currently selected, for price display. */
  #size() {
    return this.#config.sizes.find((s) => s.count === this.#state.size) ?? null;
  }

  #interpolate(template) {
    return String(template ?? '')
      .replaceAll('[selected]', String(total(this.#state)))
      .replaceAll('[total]', String(this.#state.size))
      .replaceAll('[remaining]', String(remaining(this.#state)));
  }

  #notice(message) {
    const el = this.refs.notice;
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
  }

  #clearNotices() {
    if (this.refs.notice) this.refs.notice.hidden = true;
    if (this.refs.errors) this.refs.errors.hidden = true;
  }

  /**
   * Confirmation pulse. Shoppers who have just spent a minute picking cookies need to see
   * that it landed — a silently reset grid reads as "nothing happened".
   */
  #flashAdded() {
    const el = this.refs.added;
    if (!el) return;
    el.hidden = false;
    el.classList.remove('is-in');
    void el.offsetWidth;
    el.classList.add('is-in');
    clearTimeout(this.#addedTimer);
    this.#addedTimer = setTimeout(() => el.classList.remove('is-in'), 4000);
  }

  #error(message) {
    const el = this.refs.errors;
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
  }

  #render() {
    const grouped = counts(this.#state);
    const complete = isComplete(this.#state);
    const full = total(this.#state) >= this.#state.size;

    for (const card of this.#cards()) {
      const qty = grouped[card.dataset.handle] ?? 0;

      const qtyEl = card.querySelector('[data-qty]');
      if (qtyEl) qtyEl.textContent = String(qty);

      const badge = card.querySelector('[data-count]');
      if (badge) {
        badge.textContent = String(qty);
        badge.hidden = qty === 0;
      }

      card.toggleAttribute('data-selected', qty > 0);

      const minus = card.querySelector('[data-decrement]');
      if (minus) minus.disabled = qty === 0;

      // Hard stop at capacity — over-selection must be unreachable, not merely rejected.
      const plus = card.querySelector('[data-increment]');
      if (plus) plus.disabled = full;
    }

    this.refs.counter.textContent = this.#interpolate(
      complete ? this.#config.text.complete : this.#config.text.incomplete
    );

    // Show what the box costs up front rather than making the shopper guess.
    const size = this.#size();
    if (this.refs.price) {
      this.refs.price.textContent =
        size && this.#config.text.price
          ? this.#config.text.price
              .replaceAll('[price]', size.price)
              .replaceAll('[unit]', size.unitPrice ?? '')
              .replaceAll('[total]', String(this.#state.size))
          : '';
    }

    this.refs.progress.style.setProperty(
      '--byo-progress',
      String(this.#state.size ? total(this.#state) / this.#state.size : 0)
    );

    if (!this.#busy) {
      const variantId = resolveVariantId(this.#state, this.#config);
      this.refs.submit.disabled = !complete || !variantId;
      this.refs.submit.textContent = this.#interpolate(
        complete ? this.#config.text.buttonReady : this.#config.text.buttonIncomplete
      );
    }
  }

  async submit() {
    if (this.#busy || !isComplete(this.#state)) return;

    const variantId = resolveVariantId(this.#state, this.#config);
    if (!variantId) {
      this.#error('That combination is unavailable right now.');
      return;
    }

    this.#busy = true;
    this.refs.submit.disabled = true;
    this.refs.submit.textContent = this.#config.text.buttonAdding;

    const titles = this.#titles();
    const body = new FormData();
    body.set('id', String(variantId));
    body.set('quantity', '1');
    body.set(`properties[${this.#config.text.propertyLabel}]`, describe(this.#state, titles));
    body.set('properties[_byo]', JSON.stringify(counts(this.#state)));

    // Announce the add the way Horizon does, so its cart drawer opens on its own.
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('javascript', { body }));
      const json = await response.json();
      if (json.status) throw new Error(json.description || json.message || 'Add to cart failed');

      const cartRes = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      const cart = await cartRes.json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: false, items: cart.items, source: 'byo-box-builder' },
      });

      this.#flashAdded();

      // Only reset after a confirmed success — a failure must never lose the selection.
      this.#state = createState(this.#state.size);
      this.#busy = false;
      this.#clearNotices();
      this.#render();
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } catch (error) {
      deferred.resolve({ cart: null, detail: { didError: true, source: 'byo-box-builder' } });
      this.#busy = false;
      this.#error(error.message);
      this.refs.submit.disabled = false;
      this.refs.submit.textContent = this.#config.text.buttonError;
    }
  }
}

if (!customElements.get('byo-box-builder')) {
  customElements.define('byo-box-builder', ByoBoxBuilder);
}
